import {Component, forwardRef, inject, Input, OnDestroy, OnInit} from '@angular/core';
import {ControlValueAccessor, FormControl, NG_VALUE_ACCESSOR} from '@angular/forms';
import {MatAutocompleteSelectedEvent} from '@angular/material/autocomplete';
import {MatDialog} from '@angular/material/dialog';
import {from, of, Subject, Subscription, timer} from 'rxjs';
import {debounce, switchMap} from 'rxjs/operators';
import {CmsApiService} from '../../../core/cms-api.service';
import {LoggerService} from '../../../core/logger.service';
import {TreeNode} from '../../../shared/primus-solr-tree-view/tree-node/tree-node.component';
import {PlaceTreeDialogComponent} from './place-tree-dialog.component';

interface PlaceOption {
  artifact_id: string;
  name: string;
  /** The ancestor breadcrumb (e.g. "Norge; Viken"), `null` for a
   * top-level Place. */
  path: string | null;
}

/**
 * Query-as-you-type Place picker for the Import module's "Fast verdi"
 * (fixed value) case on a group card's Place sub-block member row
 * (Place group card - the Place twin of `NamedPartySelectComponent`).
 * Emits the selected Place `artifact_id`; the run then passes it through
 * `value_is_resolved_id` so the backend uses the id directly instead of
 * resolving a name string.
 *
 * Local to `admin-import-module/` on purpose, same as its named-party
 * sibling - the native search-selector needs a real `OperationContainer`,
 * and there is only one caller. Unlike the named-party path, an
 * unresolved place name is never auto-created server-side
 * (`ImportHelper.query_place_id_from_name`) - the search here is scoped
 * to Places to keep the pick and the run in step.
 *
 * Both the as-you-type search and the by-id resolve (`writeValue`, and
 * after a hierarchy-tree pick) go through `CmsApiService` backend endpoints
 * rather than `SearchService` straight to Solr - Place hierarchy is a
 * multi-parent DB relation, not a flat Solr field, so the ancestor
 * breadcrumb (`path`, shown under the field to tell same-named Places
 * apart) is computed backend-side. The name is the place's own name, never
 * Solr's composed `artifact_name` display string.
 *
 * "Bla i steder" opens `PlaceTreeDialogComponent` - the `/places` hierarchy
 * tree - for picking a Place by drilling down instead of by name.
 */
@Component({
  selector: 'app-place-select',
  templateUrl: './place-select.component.html',
  styleUrls: ['./place-select.component.scss'],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      multi: true,
      useExisting: forwardRef(() => PlaceSelectComponent),
    },
  ],
  standalone: false,
})
export class PlaceSelectComponent implements OnInit, OnDestroy, ControlValueAccessor {
  private readonly cms: CmsApiService = inject(CmsApiService);
  private readonly logger: LoggerService = inject(LoggerService);
  private readonly dialog: MatDialog = inject(MatDialog);

  @Input() label = '';

  readonly inputControl = new FormControl<string | PlaceOption>('', {nonNullable: true});
  options: PlaceOption[] = [];
  searching = false;
  private selected: PlaceOption | null = null;
  // The id of an in-flight by-id resolve; a resolve whose id no longer
  // matches this has been superseded (input cleared, another pick) and must
  // not write its stale result back.
  private resolvingId: string | null = null;

  private readonly queries = new Subject<string>();
  private readonly subscriptions = new Subscription();
  private onChange: (value: string | null) => void = () => undefined;
  private onTouched: () => void = () => undefined;

  ngOnInit(): void {
    this.subscriptions.add(
      // An empty term (a cleared input) skips the debounce and cancels a
      // search in flight, so its late answer cannot refill the options.
      this.queries.pipe(
        debounce(term => term ? timer(250) : of(0)),
        switchMap(term => term ? from(this.search(term)) : of([] as PlaceOption[])),
      ).subscribe(options => {
        this.options = options;
        this.searching = false;
      }));

    this.subscriptions.add(this.inputControl.valueChanges.subscribe(value => {
      // A cleared / retyped input drops a previous pick until a new option
      // is chosen - never leave a stale id behind free text.
      if (typeof value === 'string') {
        this.resolvingId = null;
        if (this.selected) {
          this.selected = null;
          this.onChange(null);
        }
        const term = value.trim();
        if (term.length >= 2) {
          this.searching = true;
          this.queries.next(term);
        } else {
          this.queries.next('');
        }
      }
    }));
  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
    this.queries.complete();
  }

  /** The ancestor breadcrumb of the current pick, or `null`. */
  get pickedPath(): string | null {
    return this.selected?.path ?? null;
  }

  displayFn = (value: string | PlaceOption | null): string => {
    if (value && typeof value === 'object') {
      return value.name;
    }
    return typeof value === 'string' ? value : '';
  };

  onOptionSelected(event: MatAutocompleteSelectedEvent): void {
    this.applySelection(event.option.value as PlaceOption);
  }

  openTree(): void {
    const ref = this.dialog.open<PlaceTreeDialogComponent, void, TreeNode | undefined>(
      PlaceTreeDialogComponent, {autoFocus: false, restoreFocus: true});
    this.subscriptions.add(ref.afterClosed().subscribe(node => {
      if (node?.id) {
        // Show the node's own name immediately, then upgrade to the
        // canonical name + ancestor breadcrumb from the backend.
        this.applySelection({artifact_id: node.id, name: node.label, path: null});
        void this.resolveName(node.id);
      }
    }));
  }

  private applySelection(option: PlaceOption): void {
    this.resolvingId = null;
    this.selected = option;
    // The pick bypasses `valueChanges`, so the typed term is dropped here:
    // the empty term clears the options and cancels a search still in the
    // debounce or in flight.
    this.queries.next('');
    this.inputControl.setValue(option, {emitEvent: false});
    this.onChange(option.artifact_id);
    this.onTouched();
  }

  onBlur(): void {
    this.onTouched();
    // Free text with no matching pick is not a value.
    if (typeof this.inputControl.value === 'string' && !this.selected) {
      this.onChange(null);
    }
  }

  writeValue(value: string | null): void {
    if (!value) {
      this.resolvingId = null;
      this.selected = null;
      this.inputControl.setValue('', {emitEvent: false});
      this.queries.next('');
      return;
    }
    if (this.selected?.artifact_id === value) {
      return;
    }
    void this.resolveName(value);
  }

  registerOnChange(fn: (value: string | null) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  private async resolveName(artifactId: string): Promise<void> {
    this.resolvingId = artifactId;
    let option: PlaceOption = {
      artifact_id: artifactId,
      name: this.selected?.artifact_id === artifactId ? this.selected.name : artifactId,
      path: null,
    };
    try {
      const res = await this.cms.importModulePlaceResolve({id: artifactId});
      if (res.option) {
        option = {artifact_id: artifactId, name: res.option.name || artifactId, path: res.option.path};
      }
    } catch (e) {
      this.logger.warn(`place select: could not resolve ${artifactId}: ${e}`);
    }
    // Bail if the input moved on while we were waiting.
    if (this.resolvingId !== artifactId) {
      return;
    }
    this.resolvingId = null;
    this.selected = option;
    this.inputControl.setValue(option, {emitEvent: false});
  }

  private async search(term: string): Promise<PlaceOption[]> {
    try {
      const res = await this.cms.importModulePlaceSearch({q: term});
      return res.options.map(option => ({
        artifact_id: option.id,
        name: option.name,
        path: option.path,
      }));
    } catch (e) {
      this.logger.error(`place select: search failed for "${term}": ${e}`);
      return [];
    }
  }
}
